import React, {FC, useState, ChangeEvent, useEffect, ElementType}  from 'react';

import { useSnackbar } from 'notistack';
import { useTranslation  } from 'react-i18next';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import ListSubheader from '@mui/material/ListSubheader';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import InvertColorsIcon from '@mui/icons-material/InvertColors';

export interface BasicSelectListProps<T> {
    items: T[],
    valueKey : keyof T,
    displayKey : keyof T,

        
    initialItemsSelected?: any[],
    title? : string,

    setSelectedItems?: React.Dispatch<React.SetStateAction<any[]>>; 
    //onItemCheckedChange?: (event: ChangeEvent<HTMLInputElement>,checked: boolean, item: T ) => void;
    onItemCheckedChange?: (checked: boolean, item: T ) => void;
}

function  BasicSelectList<T>(props: BasicSelectListProps<T>) {

    const { t, i18n } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();


    const {items, valueKey, displayKey, setSelectedItems, onItemCheckedChange, 
        initialItemsSelected, title} = props;
    const [checkedItems, setCheckedItems] = useState<any[]>(initialItemsSelected || []);

    const [contextMenu, setContextMenu] = React.useState<{
        mouseX: number;
        mouseY: number;
      } | null>(null);

    const handleCheckedChange = (checked: boolean, item: T ) => {
        //console.log(checkedItems);
        const value = item[valueKey];
        const newChecked = checkedItems.filter( v => v !== value ); 
        if(checked)
            newChecked.push(value);

        setCheckedItems(newChecked);    
        if(setSelectedItems) setSelectedItems(newChecked);   
        if(onItemCheckedChange) onItemCheckedChange(checked, item);        
    }

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();
        setContextMenu(
          contextMenu === null
            ? {
                mouseX: event.clientX - 2,
                mouseY: event.clientY - 4,
              }
            : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
              // Other native context menus might behave different.
              // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
              null,
        );
      };

    const handleSelectALL = () => {
        items.forEach( itm => handleCheckedChange(true, itm) );
        setContextMenu(null);
    }

    const handleUnselectALL = () => {
        items.forEach( itm => handleCheckedChange(false, itm) );
        setContextMenu(null);
    }

    const handleInsertSelection = () => {
        items.forEach( itm => handleCheckedChange(false, itm) );
        setContextMenu(null);
    }

    const handleClose = () => {
        setContextMenu(null);
      };

    useEffect( () => {  setCheckedItems(initialItemsSelected || []);}, [initialItemsSelected] );

    return(
        <List dense component="div" role="list" onContextMenu={handleContextMenu}
        sx={{ width: '100%',  bgcolor: 'background.paper', maxHeight: '100%', overflow: 'auto' }}
        subheader={<ListSubheader>{title}</ListSubheader>}
        >
            {items.map((item, idx) =>{ 
                const labelId = `checkbox-list-label-${item[displayKey] as unknown as string}`;
                return (
                <ListItem key={idx} role="listitem" dense divider> 
                    <ListItemIcon>
                        <Checkbox
                            checked={checkedItems.indexOf(item[valueKey]) !== -1}
                            onChange={(event,checked) => handleCheckedChange(checked, item)}
                            tabIndex={-1} disableRipple inputProps={{ 'aria-labelledby': labelId }}
                        />
                    </ListItemIcon>
                    <ListItemText id={item[valueKey] as unknown as string} primary={item[displayKey] as unknown as string} />
                </ListItem>
            )} )
            }
            <Menu
                open={contextMenu !== null}
                onClose={handleClose}
                anchorReference="anchorPosition"
                anchorPosition={
                contextMenu !== null
                    ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                    : undefined
                }
            >
                <MenuItem onClick={handleSelectALL}>
                    <ListItemIcon>
                        <CheckBoxIcon fontSize="small" />
                    </ListItemIcon>
                    {t('Select ALL')}
                </MenuItem>
                <MenuItem onClick={handleUnselectALL}>
                    <ListItemIcon>
                        <CheckBoxOutlineBlankIcon fontSize="small" />
                    </ListItemIcon>
                    {t('Unselect ALL')}
                </MenuItem>      
                <MenuItem onClick={handleInsertSelection}>
                    <ListItemIcon>
                        <InvertColorsIcon fontSize="small" />
                    </ListItemIcon>
                    {t('Invert Selection')}
                </MenuItem>          
            </Menu>
        </List>
    );

}

export default BasicSelectList;